import React from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { logoutUser } from '../redux/actions/userActions';
import { isNativeApp } from '../util/platformDetection';
import { getAuth, signOut } from 'firebase/auth';

const More = ({ credentials, logoutUser }) => {
  const history = useHistory();

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      
      // ✅ Clear Authentication Tokens
      localStorage.removeItem('FBIdToken'); // Firebase authentication token
      localStorage.removeItem('LichessIdToken'); // Lichess OAuth token
      localStorage.removeItem('accessToken'); // Any additional access tokens
      localStorage.removeItem('refreshToken'); // Any refresh tokens
      
      // ✅ Clear Session Cache/Flags
      localStorage.removeItem('welcomeModalShown'); // Session-specific modal flag
      localStorage.removeItem('sessionStartTime'); // Session timing data
      localStorage.removeItem('lastActiveTab'); // Current session tab state
      localStorage.removeItem('currentGameId'); // In-progress game session
      localStorage.removeItem('lichessOauthConfig'); // OAuth configuration data
      localStorage.removeItem('selectedTabFilter'); // Current session filter state
      
      // ✅ Clear Cached User Data (must be fetched again on login)
      localStorage.removeItem('userProfile'); // Cached user profile data
      localStorage.removeItem('userEmail'); // Cached email
      localStorage.removeItem('userSettings'); // Server-synced user settings
      
      // ✅ Clear Context/App State Sync Flags
      localStorage.removeItem('syncStatus'); // Data synchronization status
      localStorage.removeItem('firebaseInit'); // Firebase initialization flags
      localStorage.removeItem('authRedirectPending'); // Authentication redirect state
      
      // ✅ Clear Temporary Session Data
      localStorage.removeItem('analysisImportPGN'); // Temporary analysis data
      localStorage.removeItem('currentPositionFen'); // Current game position
      localStorage.removeItem('portalSeenGameIds'); // Session-specific game tracking
      localStorage.removeItem('hiddenSpamGames'); // Admin session data
      localStorage.removeItem('hiddenReportedComments'); // Admin session data
      
      // 🛑 PRESERVE User Preferences (DO NOT REMOVE):
      // - boardTheme: User's preferred board theme
      // - customColors: Custom board color settings
      // - showCoordinates: Board coordinate display preference
      // - showLegalMoves: Legal move highlighting preference
      // - hideLiveChat: Chat visibility preference
      // - isSidebarCollapsed: Sidebar state preference
      // - stockfishUiVersion: Stockfish UI preferences
      // - stockfishArrowSetting: Arrow display preferences
      // - stockfishAnalysisDepth: Analysis depth preferences
      // - stockfishRecalcThreshold: Recalculation threshold preferences
      // - viewGame_showStockfish: ViewGame Stockfish preference
      // - viewGame_showStockfishEval: ViewGame eval bar preference
      // - viewGame_showArrows: ViewGame arrows preference
      // - viewGame_showStockfishIcons: ViewGame icons preference
      // - addGame_showStockfish: AddGame Stockfish preference
      // - addGame_showStockfishEval: AddGame eval bar preference
      // - addGame_showStockfishIcons: AddGame icons preference
      // - showStockfish: General Stockfish preference
      // - showStockfishIcons: General Stockfish icons preference
      // - boardSize: User's preferred board size
      // - chessMenuCollapsed: Chess menu state preference
      // - chessChatCollapsed: Chess chat state preference
      // - isOpponentHidden: Opponent visibility preference
      // - preferredDifficulty: User's preferred difficulty setting
      // - gameMode: User's preferred game mode
      // - engineDifficulty: User's preferred engine difficulty
      // - showUndoButton: Undo button visibility preference
      // - portalDifficulty: Portal difficulty filter preference
      
      // Redirect to login page
      history.push('/login');
    } catch (error) {
      // console.error('Error logging out:', error);
    }
  };

  const menuItems = [
    {
      id: 'search',
      icon: <i className="fa-solid fa-magnifying-glass"></i>,
      label: 'Search',
      action: () => history.push('/search')
    },
    {
      id: 'faq',
      icon: <i className="fa-solid fa-circle-question"></i>,
      label: 'FAQ',
      action: () => history.push('/faq')
    },
    {
      id: 'settings',
      icon: <i className="fa-solid fa-gear"></i>,
      label: 'Settings',
      action: () => history.push('/mobile-settings')
    },
    {
      id: 'about',
      icon: <i className="fa-solid fa-circle-info"></i>,
      label: 'About',
      action: () => history.push('/about')
    },
    {
      id: 'contact',
      icon: <i className="fa-solid fa-envelope"></i>,
      label: 'Contact',
      action: () => history.push('/contact')
    },
    {
      id: 'logout',
      icon: <i className="fa-solid fa-sign-out-alt"></i>,
      label: 'Logout',
      action: handleLogout
    }
  ];

  return (
    <div style={{
      width: '100vw',
      height: 'calc(100dvh - 55px)', // Account for bottom navigation
      backgroundColor: '#1a1a1a',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top)', // iPhone notch safe area
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333',
        padding: '15px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h2 style={{
          color: 'gold',
          margin: 0,
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          More Options
        </h2>
        <button
          onClick={() => history.goBack()}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#888',
            fontSize: '28px',
            cursor: 'pointer',
            padding: '5px'
          }}
        >
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>


      {/* Full-Width Menu Buttons */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: 'env(safe-area-inset-bottom)' // iPhone gesture area
      }}>
        {menuItems.map((item, index) => (
          <button
            key={item.id}
            onClick={item.action}
            style={{
              width: '100%',
              backgroundColor: '#1a1a1a',
              border: 'none',
              borderTop: index === 0 ? 'none' : '1px solid #333',
              padding: '12px 20px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              transition: 'all 0.2s ease',
              textAlign: 'left',
              fontSize: '18px'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#2a2a2a';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#1a1a1a';
            }}
          >
            <div style={{
              fontSize: '32px',
              minWidth: '50px',
              textAlign: 'center'
            }}>
              {item.icon}
            </div>
            <div style={{ 
              flex: 1,
              fontSize: '20px',
              fontWeight: 'bold'
            }}>
              {item.label}
            </div>
            <div style={{
              fontSize: '24px',
              color: '#666'
            }}>
              ›
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  credentials: state.user.credentials
});

const mapDispatchToProps = {
  logoutUser
};

export default connect(mapStateToProps, mapDispatchToProps)(More);